import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Heart, Truck, Award } from "lucide-react";
import PageHeader from "../components/PageHeader";
import Reveal from "../components/Reveal";
import { TRUCK_IMAGE, HERO_IMAGES } from "../data/images";

export default function About() {
  return (
    <div data-testid="page-about">
      <PageHeader
        eyebrow="Our story"
        title="Built on cravings,"
        accent="cooked with love."
        subtitle="An Indian street-food egg celebration parked in Clayton, Melbourne."
        image={HERO_IMAGES[0]}
      />

      <section className="max-w-7xl mx-auto px-4 md:px-8 py-12">
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-20 items-center">
          <Reveal>
            <div className="hover-zoom rounded-3xl overflow-hidden aspect-[4/5]">
              <img src={TRUCK_IMAGE} alt="Taste of Egg food truck at night" className="w-full h-full object-cover" />
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <span className="eyebrow">The beginning</span>
            <h2 className="mt-3 text-3xl md:text-5xl font-extrabold leading-tight">
              From Indian streets <span className="font-script text-yellow-brand text-4xl md:text-6xl">to Clayton.</span>
            </h2>
            <p className="text-white/65 mt-6 leading-relaxed">
              Taste of Egg started with a craving — the kind you only feel after midnight, standing under a yellow light at a chaotic Mumbai or Surat street stall, watching a vendor smash eggs onto a sizzling tawa. That craving travelled with us all the way to Melbourne.
            </p>
            <p className="text-white/65 mt-4 leading-relaxed">
              We didn't want to dilute it. We didn't want to "fusion" it. We wanted it real — the same fiery bhurji, the same buttery half-fry, the same smoky goti gotalo our families have made for generations. So we built a truck around it.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 md:px-8 py-20">
        <div className="grid md:grid-cols-3 gap-6">
          {[
            { icon: <Heart />, title: "Passion first", body: "Every plate is hand-built. We won't pre-cook your eggs." },
            { icon: <Truck />, title: "Truck culture", body: "Casual, fast, and unapologetically loud about flavour. One truck. One mission." },
            { icon: <Award />, title: "Authentic recipes", body: "Generations-old masalas. No food-court shortcuts." },
          ].map((b, i) => (
            <Reveal key={i} delay={i * 0.08}>
              <div className="card-dark p-8 h-full">
                <div className="w-12 h-12 rounded-full bg-yellow-brand/15 text-yellow-brand flex items-center justify-center mb-5">
                  {b.icon}
                </div>
                <h3 className="text-xl font-extrabold">{b.title}</h3>
                <p className="text-white/60 mt-2 leading-relaxed">{b.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 md:px-8 py-12">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
          {[
            { kind: "img", src: TRUCK_IMAGE, alt: "Taste of Egg food truck", span: "col-span-2 row-span-2 aspect-square" },
            { kind: "stat", n: "30+", l: "Egg dishes" },
            { kind: "stat", n: "1000+", l: "Eggs cracked / week" },
            { kind: "stat", n: "5.0", l: "Star reviews" },
            { kind: "stat", n: "VIC", l: "Free-range eggs" },
          ].map((t, i) => (
            <Reveal key={i} delay={i * 0.05}>
              {t.kind === "img" ? (
                <div className={`hover-zoom rounded-2xl overflow-hidden ${t.span}`}>
                  <img src={t.src} alt={t.alt} loading="lazy" className="w-full h-full object-cover" />
                </div>
              ) : (
                <div className="card-dark p-5 aspect-square flex flex-col justify-between">
                  <span className="text-yellow-brand uppercase tracking-widest text-[10px] font-bold">Numbers</span>
                  <div>
                    <div className="text-5xl md:text-6xl font-extrabold text-white leading-none">{t.n}</div>
                    <div className="text-white/55 text-xs mt-2 uppercase tracking-widest">{t.l}</div>
                  </div>
                </div>
              )}
            </Reveal>
          ))}
        </div>
      </section>

      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 md:px-8 text-center">
          <Reveal>
            <h2 className="text-3xl md:text-5xl font-extrabold leading-tight balanced">
              Come hungry. Leave smiling.{" "}
              <span className="font-script text-yellow-brand text-4xl md:text-6xl">That's the deal.</span>
            </h2>
            <Link to="/menu" className="btn-primary mt-8 inline-flex" data-testid="about-menu-cta">
              Explore the menu <ArrowRight className="w-4 h-4" />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
